import * as THREE from 'three';
import type { SketchCircleObject } from '../../../mesh/sketch/SketchCircleObject';
import type { ProjectionResult } from '../model/ProjectionResult';
import type {
	ReferenceDrivenCircleRelationRuntimeRecord,
	SketchEntityReferenceRecord,
} from '../model/SketchEntityReferenceRecord';
import { resolveSourceRefEntityId } from '../model/SketchEntityReferenceRecord';
import type { ExecutionDiagnostic } from '../model/SketchEntityReferenceDiagnostics';
import { BaseReferenceDrivenRelationService } from './BaseReferenceDrivenRelationService';
import { ReferenceDrivenCircleCoincidentConstraint } from './constraints/ReferenceDrivenCircleCoincidentConstraint';
import type {
	ReferenceDrivenCircleRelationBinding,
	ReferenceDrivenCircleRelationAttachResult,
} from '../../../../types/reference/sketch-entity-reference/ReferenceDrivenCircleRelationService';

export type {
	ReferenceDrivenCircleRelationAttachResult,
} from '../../../../types/reference/sketch-entity-reference/ReferenceDrivenCircleRelationService';

/**
 * 参考驱动圆关系服务。
 */
export class ReferenceDrivenCircleRelationService
	extends BaseReferenceDrivenRelationService<
		ReferenceDrivenCircleRelationRuntimeRecord,
		ReferenceDrivenCircleRelationBinding
	> {
	/**
	 * 挂载圆重合约束。
	 *
	 * @param input 挂载输入。
	 * @returns 挂载结果。
	 */
	public attach(input: {
		reference: SketchEntityReferenceRecord;
		projectionResult: Extract<ProjectionResult, { projectionResultKind: 'circle' }>;
		targetCircle: SketchCircleObject;
		runtimeSourceName?: string;
	}): ReferenceDrivenCircleRelationAttachResult {
		this.detach({
			referenceId: input.reference.referenceId,
			targetCircle: input.targetCircle,
		});

		const record: ReferenceDrivenCircleRelationRuntimeRecord = {
			referenceId: input.reference.referenceId,
			relationKind: 'coincident',
			driveMode: 'projection-drives-target',
			sourceRefEntityId: resolveSourceRefEntityId(input.reference.sourceDescriptor),
			targetEntityId: input.targetCircle.persistentId,
			constraintId: -1,
		};
		const constraint = new ReferenceDrivenCircleCoincidentConstraint({
			referenceId: input.reference.referenceId,
			projectedCenterWorld: new THREE.Vector3(...input.projectionResult.geometryPayload.centerWorld),
			projectedRadius: input.projectionResult.geometryPayload.radius,
			sourceDisplayLabel: input.reference.sourceDisplayLabel,
			runtimeSourceName: input.runtimeSourceName,
		});
		input.targetCircle.constraints.addRelation(constraint);
		record.constraintId = constraint.id;
		this.setBinding(input.reference.referenceId, {
			constraint,
			record,
			targetCircle: input.targetCircle,
		});

		/** 圆心与半径由批次统一参数求解写回，挂载阶段只建立公式关系。 */
		return {
			record,
			diagnostics: [],
		};
	}

	/**
	 * 拆除圆关系。
	 *
	 * @param input 拆除输入。
	 * @returns 诊断信息。
	 */
	public detach(input: {
		referenceId: string;
		targetCircle?: SketchCircleObject;
	}): ExecutionDiagnostic[] {
		const binding = this.getBinding(input.referenceId);
		if (binding) {
			binding.targetCircle.constraints.removeRelation(binding.record.constraintId);
			this.deleteBinding(input.referenceId);
			return [];
		}

		if (input.targetCircle) {
			const constraint = input.targetCircle.constraints.getRelations().find((candidate) => (
				candidate instanceof ReferenceDrivenCircleCoincidentConstraint
				&& candidate.referenceId === input.referenceId
			));
			if (constraint) {
				input.targetCircle.constraints.removeRelation(constraint.id);
			}
		}
		return [];
	}
}
